import { z } from 'zod';
import { boardSchema } from './board';
import { cardSchema } from './card';
import { cardListRefSchema } from './card-detail';
import { firstPartyIntegrationKeySchema } from './first-party-integration';
import { ulidSchema } from './id';

/**
 * Global search — the contract layer for block **B20**, the MVP slice.
 *
 * `BE-20.1` (`FR-SEARCH-018`, restrict results to what the caller may see) and
 * `BE-20.2` (`FR-SEARCH-015`, the instant grouped typeahead), reached from
 * `FR-SEARCH-014`'s always-present input. One route — `GET /search?q=<text>` —
 * answering `{ cards, boards }`.
 *
 * ---------------------------------------------------------------------------
 * WHAT IS ACTUALLY NEW ABOUT THIS FILE, HAVING FIRST CHECKED WHAT IS NOT
 * ---------------------------------------------------------------------------
 * ⚠ **It is NOT "the first file here that is both an input contract and a view
 * contract plus a pure function".** That claim was on hand when this file was
 * written and it is false; it is recorded here as refuted rather than dropped,
 * because `./index`'s header and `./label`'s entry both carry corrections of
 * precisely this failure — a superlative that one `grep` would have settled.
 * The `grep`:
 *
 *     grep -l "z.strictObject" packages/shared/src/*.ts | grep -v test
 *     grep -l "^export function" packages/shared/src/*.ts | grep -v test
 *
 * `./comment` (B16) and `./notification` (B17) are in **both** lists, and both
 * declare views as well. Each is exactly the shape this file was going to
 * claim to invent, and one of them — B16's cursor codec — is the stated
 * precedent for the pure function below.
 *
 * The property that **is** new, and it is a sharper one:
 *
 *   ⚠ **`SEARCH_QUERY_MIN_LENGTH` is the first bound in this package that
 *   encodes a measured property of the database's query planner rather than a
 *   number a requirement states.** Every other `.min()`/`.max()` here restates
 *   a column width (`CARD_TITLE_MAX_LENGTH`), a product rule
 *   (`COMMENT_BODY_MAX_LENGTH`) or a format (`ulidSchema`). This one exists
 *   because `%ab%` contains no complete trigram and `%abc%` does — see the
 *   constant. `FR-SEARCH-015` AC1 explicitly declines to supply the number:
 *   *"any minimum-character threshold before results fire is unverified —
 *   decide and state it"*. `OQ-7` is that decision and this constant is where
 *   it is stated.
 *
 *   The consequence a reader must not miss: **this constant and
 *   `db/postgres/migrations/0019_search_trgm_indexes.sql` are one decision in
 *   two files.** Change the index and this number may be wrong; change this
 *   number downwards and the route silently starts issuing sequential scans
 *   over every card in the product. Neither file can be reviewed alone.
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ THE PERMISSION SCOPE IS NOT IN THIS FILE, AND MUST NEVER BE
 * ---------------------------------------------------------------------------
 * `FR-SEARCH-018` is half of this block and there is not one line of it here.
 * The scope is `isBoardSearchableBy` in
 * `apps/server/src/boards/permissions.ts` — SQL, composed into the `WHERE` of
 * both statements, per SC-5 (*scope inside the query, never filter permissions
 * in application code afterwards*).
 *
 * That is the same split `./mention` draws and for the same mechanical reason:
 * **this package is bundled into `apps/web`.** A predicate here would need a
 * database handle in the browser; a *list* of readable board ids here would be
 * the leak `FR-SEARCH-018`'s edge case forbids (*"result counts must not
 * reveal the existence of hidden matches"*) shipped to the client in person.
 * What crosses the wire is the query in and the permitted rows out, and
 * nothing in between.
 *
 * ⚠ The one permission fact worth stating here, because it decides what a
 * client may assume about the payload: **a public board the caller is not a
 * member of is not in it.** `FR-SEARCH-018` AC3 — *"Given a public board the
 * user is not a member of, when they search a phrase on it, then it is **not**
 * returned"* — and `OQ-8` settled: a public board is reachable by link, not
 * discoverable by search. So `searchBoardResultSchema.visibility` may read
 * `'public'`, but only for a board the caller is *on*.
 *
 * ---------------------------------------------------------------------------
 * TWO GROUPS, AND BOTH EXCLUSIONS ARE DELIBERATE
 * ---------------------------------------------------------------------------
 * `cards` and `boards`, matched on **titles only**.
 *
 *   - **No `workspaces` or `members` group**, though `FR-SEARCH-015`'s
 *     behaviour sentence says "cards, boards, and workspaces or members where
 *     relevant". `BE-20.2`'s item line is "card and board titles", and "where
 *     relevant" names no requirement that decides when. A group with no
 *     acceptance criterion is a shape a later block would have to change; two
 *     groups with two criteria is what MVP asked for.
 *   - **No description matching.** `FR-SEARCH-021` — free text across card
 *     title, description, comments and checklists — is **V1** and is B33's,
 *     the four-source text half that widens B20's query, and
 *     `FR-SEARCH-017`'s description toggle rides on the results page that
 *     comes with it. `0019` indexes the two title columns and nothing else,
 *     for the same reason.
 *
 * ---------------------------------------------------------------------------
 * ⚠ THERE IS NO COUNT FIELD ANYWHERE IN THE RESPONSE, AND ITS ABSENCE IS THE
 * REQUIREMENT
 * ---------------------------------------------------------------------------
 * No `total`, no `cardCount`, no `hasMore`, no `truncated`. `FR-SEARCH-018`'s
 * edge case is one sentence — *"Result counts must not reveal the existence of
 * hidden matches"* — and a total is the field that reveals them: any total
 * honest enough to be useful is computed before the group limit and therefore
 * over a set the caller cannot see, and any total narrow enough to be safe is
 * `results.length`, which the client already has.
 *
 * **Omitting the field makes the requirement unbreakable rather than carefully
 * satisfied**, which is the whole argument for doing it this way. A `total`
 * that is correct today is one permission-predicate refactor away from being a
 * probe, and nothing about the response shape would say so. `FR-SEARCH-015`
 * asks for no total either — its "Advanced search" affordance (`FR-SEARCH-017`,
 * V1, B33) is where a full result set is even addressable.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS FILE DELIBERATELY DOES NOT DECLARE
 * ---------------------------------------------------------------------------
 *   - **Operator syntax.** `FR-SEARCH-020`–`FR-SEARCH-027` (`board:`,
 *     `list:`, `member:`, `@me`, `is:starred`, `sort:`, `due:`, …) are all V1
 *     and **B34's**, not B33's — `parseSearchQuery`, appended near the bottom
 *     of this file, is that grammar's pure parser, meant to be consumed by
 *     `GET /search/results` alone once a later phase wires it in — nothing
 *     does yet. `q` here is free text and is matched
 *     literally, metacharacters and all — see `escapeLikePattern`. A caller
 *     sending `board:Marketing` to *this* route still searches for the
 *     *string* `board:Marketing`, by construction rather than by an
 *     unfinished feature: this dropdown never calls the operator parser at
 *     all, which is `FR-SEARCH-020` AC4's own unrecognised-prefix fallback
 *     arriving here for a different reason than the one it names.
 *   - **A `sort` parameter.** `FR-SEARCH-019` is V1; its default — recency —
 *     is what B20 implements unconditionally, and the *control* that overrides
 *     it comes with the results page. A `?sort=` defaulting to `created` would
 *     be a wire commitment to an enum whose other members nobody has specified,
 *     which is exactly the trap `./board-filter` refused for `?mode=`.
 *   - **A `mode` parameter, or any facet.** `?mode=all`, `?boards=`,
 *     `?archived=true` and `?includeClosed=true` are all **422s** from the
 *     strict object below, and that is the intended answer: `FR-SEARCH-028`'s
 *     archived-and-closed toggles are V1 and their default is *excluded*, so
 *     the honest MVP surface is one that offers no way to ask.
 *   - **Any paging.** No cursor, no `limit`, no offset — the dropdown caps
 *     each group and the full set lives on `FR-SEARCH-017`'s page. ⚠ This is
 *     the one place `./comment`'s keyset codec is a **precedent not to
 *     follow**: a comment stream is unbounded and must be walkable, a
 *     typeahead is a fixed handful and must not be. Adding a cursor here would
 *     hand a caller a way to page through everything they can see, one
 *     `SEARCH_GROUP_LIMIT` at a time, which is the results page built by
 *     accident and without its permission review.
 *   - **The jump-to panel.** `FR-SEARCH-016` (starred and recently viewed
 *     boards on an *empty* query) is V1 and B32's. An empty `q` is a 422 here,
 *     not an alternative response shape.
 */

// ---------------------------------------------------------------------------
// Bounds.
//
// ⚠ Neither bound below imports a column constant, and that is this package's
// standing rule rather than an oversight — `./card`'s note states it in full:
// *import the constant that describes YOUR column if one exists, declare it if
// none does, and never share one across two columns*. A search query is not a
// column at all. It is compared against two of them, which is a third thing
// again, and the arithmetic below is what makes that concrete.
// ---------------------------------------------------------------------------

/**
 * The shortest query that produces results — `OQ-7`, answered **3**.
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ THIS IS A MEASUREMENT, NOT A PREFERENCE, AND THE CAUSE IS STRUCTURAL
 * ---------------------------------------------------------------------------
 * Measured in `molt_v1_probe` on a 40,005-row `varchar(512)` table carrying the
 * same `gin (title gin_trgm_ops)` index
 * `db/postgres/migrations/0019_search_trgm_indexes.sql` creates, planner free
 * to choose, rare patterns so row counts do not confound the plan:
 *
 *     title ILIKE '%zq%'    Seq Scan, 40,004 rows removed by filter,
 *                           455 shared buffers, 22.038 ms
 *     title ILIKE '%zqx%'   Bitmap Index Scan on the trigram index,
 *                           4 shared buffers, 0.059 ms
 *
 * Roughly **370x**, and the cause is not statistical: **pg_trgm extracts whole
 * trigrams from the *pattern*, and a two-character pattern contains no complete
 * trigram.** There is no index key to look up, so the scan degenerates to
 * reading every row. `%abc%` yields the single trigram `abc` and the index
 * answers in four buffers.
 *
 * ⚠ **"The index is usable at two characters" is a true sentence that means
 * nothing**, and it is the sentence that will tempt somebody to lower this
 * number. Forcing the planner with `SET enable_seqscan = off` does produce a
 * Bitmap Index Scan for `%ab%` — by scanning every trigram key in the index
 * instead of every row in the table. The planner chooses the sequential scan
 * because it is the *cheaper* of the two, not because the index was
 * unavailable.
 *
 * So three is the shortest query the block's own named technology can serve,
 * and that is the whole argument. It is not a guess at when a user has typed
 * enough to mean something.
 *
 * ---------------------------------------------------------------------------
 * ⚠ A SHORTER QUERY IS A **422**, NOT A 200 WITH EMPTY GROUPS
 * ---------------------------------------------------------------------------
 * `FR-SEARCH-015` AC4: *"Given a query with no matches, when typing stops,
 * then an explicit empty state is shown rather than a blank panel."* A 200
 * carrying two empty arrays **is** "no matches", so answering that for `ab`
 * would render "No results for ab" — telling the user their search failed when
 * it never ran. The client must be able to tell "still typing" from "nothing
 * found", and a status code is the only thing in this response that can.
 *
 * That is also `./board-filter`'s standing: strictness that produces a 422 is
 * how a client learns it asked a question the route does not answer, rather
 * than reading a legitimate-looking answer to a different one.
 *
 * ⚠ **The frontend must import this constant** (`FE-20.2`) rather than
 * hardcoding a second 3 to decide when to start firing requests. Two copies
 * would disagree on the day a later measurement moves it, and the symptom
 * would be an input that fires a request per keystroke and gets a 422 every
 * time — a working search with a red network tab, which nobody looks at.
 */
export const SEARCH_QUERY_MIN_LENGTH = 3;

/**
 * The longest query that can possibly match anything, in characters.
 *
 * ⚠ **Deliberately its own constant, and deliberately NOT
 * `CARD_TITLE_MAX_LENGTH` imported.** Both are 512 today, so a reader scanning
 * the two files sees one number and suspects duplication — the same coincidence
 * `./card`'s note warns about between `boards.title`, `lists.title` and
 * `cards.title`.
 *
 * They are one number by **derivation**, not by sharing, and the derivation is
 * what makes them separable. Matching is containment: `title ILIKE '%q%'` can
 * only be satisfied when `length(q) <= length(title)`. Two columns are matched,
 * so the bound past which the answer is *provably* empty is
 *
 *     max(CARD_TITLE_MAX_LENGTH, BOARD_TITLE_MAX_LENGTH)   // = max(512, 512)
 *
 * — the **larger** of the two, not either one. Adopt `CARD_TITLE_MAX_LENGTH`
 * and the day `boards.title` widens to 1024 the route starts refusing board
 * queries that would have matched; adopt `BOARD_TITLE_MAX_LENGTH` and the
 * mirror image happens.
 *
 * ⚠ **The `max()` is written out above and deliberately not evaluated in
 * code.** Computing it would make a *wire bound the client imports* change as
 * a silent side effect of a column migration, which is the coupling this
 * package's constant rule exists to prevent — and a wire contract should move
 * when somebody decides it moves. So: **512 today, and the instruction for the
 * day either column widens is the line above** — take the larger of the two,
 * here, on purpose, with `search.test.ts` updated to say so. This is the one
 * constant in this file that a schema change elsewhere can invalidate without
 * anything failing.
 *
 * This bound can never lose a result: everything it refuses would have returned
 * nothing. It is a cheap refusal rather than a product rule, and that is the
 * only reason it is safe for it to be a hard 422 — a cap chosen for taste would
 * need `FR-SEARCH-*` authority, and there is none.
 *
 * It is **not** a transport defence. Node's 16 KB header budget refuses an
 * over-long querystring with a **431** long before this, exactly as
 * `./board-filter`'s facets record; this is the boundary of *meaning*, not of
 * size.
 */
export const SEARCH_QUERY_MAX_LENGTH = 512;

/**
 * How many rows each group carries — `FR-SEARCH-015`'s "a small number of top
 * results" and its edge case, *"The dropdown caps each group at a handful of
 * rows; the full set is only available on the results page
 * (`FR-SEARCH-017`)."*
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ **PER GROUP**, WHICH MEANS TWO STATEMENTS WITH TWO `LIMIT`s — NEVER ONE
 * RANKED UNION
 * ---------------------------------------------------------------------------
 * The economy a reader reaches for is one query over both tables with one
 * `ORDER BY` and one `LIMIT`. It is wrong, and it fails in a way no assertion
 * about "five results came back" can see: a caller whose fragment matches
 * fifty recently-created cards and one board gets **five cards and no board
 * at all**, because the boards lost the global ordering. `FR-SEARCH-015` AC1
 * asks for *grouped* results and its edge case caps *each group*; a group that
 * can be crowded out of existence by the other group is not a group.
 *
 * Two statements is also what makes the permission predicate reviewable:
 * `isBoardSearchableBy` is composed into each one on its own terms, over a
 * `FROM` that contains `boards` in one case and `cards` joined to `boards` in
 * the other, rather than into a union whose arms a reader must check
 * separately anyway.
 *
 * Five, not eight or ten: the dropdown is a *jump-to*, and `FR-SEARCH-014`
 * puts it in the top bar of every screen where it overlays whatever the user
 * was reading. Two groups of five is one screenful on a laptop without
 * scrolling, which is the affordance `FR-SEARCH-017`'s page exists to
 * escalate from.
 */
export const SEARCH_GROUP_LIMIT = 5;

// ---------------------------------------------------------------------------
// The pure function.
//
// One, and it is here rather than in the server for `./comment`'s reason: an
// escaping rule is a **wire-format** concern, and the client that builds a
// highlight over the matched substring has to agree with the server about what
// the user's text meant. `./mention`'s split is the same idea from the other
// side — the parser is shared, the resolution is not.
// ---------------------------------------------------------------------------

/**
 * Make a user's raw text safe to drop inside a `LIKE`/`ILIKE` pattern, by
 * escaping the three characters SQL's pattern language reserves.
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ THIS IS NOT AN INJECTION DEFENCE AND MUST NOT BE READ AS ONE — WHICH IS
 * WHY IT IS EASY TO CONCLUDE IT IS UNNECESSARY
 * ---------------------------------------------------------------------------
 * Drizzle parameterises the value; nothing here is about escaping quotes and
 * nothing about SQL injection. The bug is one level up: **the parameterised
 * value is still a `LIKE` *pattern***, and `%` and `_` are operators inside it.
 * A reviewer who checks that the query is parameterised has checked the wrong
 * property and will find nothing wrong.
 *
 * Measured in `molt_v1_probe`, on a fixture carrying `axc`, `a_c`,
 * `rollout 50% complete` and `rollout 5012 complete` among 40,000 filler rows:
 *
 *     title ILIKE '%a_c%'    3 rows      title ILIKE '%a\_c%'    1 row
 *     title ILIKE '%50%%'    1,198 rows  title ILIKE '%50\%%'    1 row
 *
 * So unescaped, a user typing `a_b` matches `axb`, and a user typing `50%`
 * matches **essentially the whole table** — a "search" that returns everything,
 * with a 200 and a valid body. Nothing anywhere fails.
 *
 * ---------------------------------------------------------------------------
 * ⚠ THE ORDERING TRAP, AND WHY THIS IMPLEMENTATION CANNOT FALL INTO IT
 * ---------------------------------------------------------------------------
 * The obvious implementation is three sequential `.replace()` calls, and then
 * **the backslash must be escaped first** or the escapes get escaped. Traced on
 * the input `a\_b`:
 *
 *     backslash first:  a\_b  ->  a\\_b   ->  a\\\_b   (correct: a, \, _, b)
 *     underscore first: a\_b  ->  a\\_b   ->  a\\\\_b  (wrong: a, \, \, ANY, b)
 *
 * The wrong order does not throw and does not return an obviously broken
 * string; it returns a pattern that matches a different set of rows.
 *
 * **The single-pass character class below makes the ordering unrepresentable.**
 * Each character of the input is examined exactly once, so a backslash the
 * function *emits* is never re-examined and there is no second pass to get
 * wrong. That is the reason for this spelling rather than the sequential one,
 * and it is worth keeping: a maintainer "simplifying" this into three
 * `.replace()` calls reintroduces an ordering requirement that currently does
 * not exist.
 *
 * ---------------------------------------------------------------------------
 * ⚠ THE ESCAPE CHARACTER IS THE BACKSLASH BY DEFAULT, AND ONE CLAUSE TURNS
 * THIS FUNCTION OFF
 * ---------------------------------------------------------------------------
 * Postgres' `LIKE` uses `\` as the escape character unless an `ESCAPE` clause
 * says otherwise, and Drizzle's `ilike()` emits no `ESCAPE` clause — so the
 * default applies and the output of this function is interpreted as intended.
 * A future caller who writes `ESCAPE ''` (legal, and it means *no escape
 * character at all*) silently restores every failure measured above, with this
 * function still in the call path looking as though it is doing its job.
 *
 * The pattern also travels as a **bind parameter**, so `standard_conforming_
 * strings` never gets a chance to reinterpret the backslash on the way in;
 * what this function emits is what the pattern matcher sees.
 *
 * ⚠ **Escaping does not cost the index.** `title ILIKE '%a\_c%'` planned as a
 * Bitmap Index Scan on `idx_cards_title_trgm`'s probe equivalent — 3 rows from
 * the index, 2 removed by the heap recheck, 1 returned. The escape is honoured
 * by the recheck rather than by trigram extraction, so the pattern stays
 * indexed and stays correct. Measured, because "the index cannot handle
 * escapes" would have been a reason to solve this a different way.
 *
 * ⚠ **It does not wrap the value in `%...%`.** The caller does that, after
 * escaping, because the wrapping percent signs are the *only* two that must
 * stay operators. A helper that returned the finished `'%' + escaped + '%'`
 * would be more convenient and would leave nowhere to express a future
 * anchored match (`FR-SEARCH-021`'s `name:` operator, B33).
 */
export function escapeLikePattern(value: string): string {
  return value.replace(/[\\%_]/g, (character) => `\\${character}`);
}

// ---------------------------------------------------------------------------
// Input — what the route ACCEPTS.
// ---------------------------------------------------------------------------

/**
 * The querystring of `GET /search`.
 *
 * ⚠ **`z.strictObject`**, so every parameter this block does not implement is a
 * 422 rather than a silently ignored key: `?mode=all`, `?sort=due`,
 * `?boards=<id>`, `?archived=true`, `?limit=20`. Each of those is a real
 * `FR-SEARCH-*` control owned by a later block, and each would otherwise return
 * a **correct-looking unfiltered answer** to a caller who believes it was
 * applied — a search that quietly ignores "exclude closed boards" is worse than
 * one that refuses it, because the user acts on the result. This is
 * `./board-filter`'s argument for `?state=archived` in a second costume.
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ `.trim()` RUNS BEFORE `.min()`, AND THE ORDER IS LOAD-BEARING — MEASURED
 * AGAINST THIS WORKSPACE'S ZOD, NOT ASSUMED
 * ---------------------------------------------------------------------------
 * Without the trim running first, `'  a  '` is five characters and passes a
 * three-character minimum, and the route then builds `%a%` — one character —
 * which is the sequential scan `SEARCH_QUERY_MIN_LENGTH` exists to prevent,
 * reached through the check meant to prevent it.
 *
 * Zod **4.4.3** (this workspace's pin), probed directly:
 *
 *     z.string().trim().min(3)   '  ab  '  -> 422 too_small
 *                                '  abc  ' -> OK, data === 'abc'
 *                                '     '   -> 422 too_small
 *     z.string().min(3).trim()   '  a  '   -> OK, data === 'a'      ← the bug
 *
 * The reversed spelling is the failure written out: it accepts, and it hands
 * the handler a one-character query. So the chain order below is a contract,
 * not a style, and the parsed value arrives at the route **already trimmed** —
 * the handler must not trim again and must not use `request.query.q` raw from
 * anywhere else.
 *
 * ⚠ Trimming strips the ends only. `'  a b  '` parses to `'a b'`, three
 * characters, and is **accepted** — interior whitespace counts towards the
 * minimum because it counts towards the trigram, and `' b '` is as real a
 * trigram as `abc`.
 *
 * There is no `.optional()` and no default: an absent `q` is a 422, as is
 * `?q=`. `FR-SEARCH-016`'s empty-query jump-to panel is a **different
 * response** owned by a later block, so an empty query has no meaning here that
 * a default could stand in for.
 */
export const searchQuerySchema = z.strictObject({
  q: z
    .string()
    .trim()
    .min(SEARCH_QUERY_MIN_LENGTH)
    .max(SEARCH_QUERY_MAX_LENGTH)
    .describe(
      'The text to search for, matched as a case-insensitive substring of ' +
        'card and board titles. Minimum 3 characters after trimming — a ' +
        'shorter query is a 422, not an empty result set, because pg_trgm ' +
        'cannot serve a pattern with no complete trigram. Matched literally: ' +
        'there are no operators yet, and % and _ are escaped rather than ' +
        'interpreted.',
    ),
});

/**
 * The parsed query as the route receives it — `request.query`.
 *
 * ⚠ Derived with `z.infer`, this package's standing rule, and never declared
 * beside the schema. Checked against the DOM's globals before being named:
 * `lib.dom.d.ts` declares no `SearchQuery` and no `SearchResults`, unlike
 * `Notification` and `Comment`, which it declares and which `./notification`
 * and `./comment` both document as collisions. Nothing in this file needs that
 * treatment.
 */
export type SearchQuery = z.infer<typeof searchQuerySchema>;

// ---------------------------------------------------------------------------
// Views — what the route RETURNS.
//
// Both result shapes **extend a shipped view on a copy** rather than restating
// its fields, and neither widens the base. `./index`'s rule in one line: extend
// on a copy for what one route needs; widen the base only for what every route
// serving that noun should carry. A search result is emphatically the former —
// `cardSchema` serialises B12's four routes and the board face, and nothing
// there wants a nested board.
//
// ⚠ The trade that makes composition right here rather than a narrow copy is
// that **the payload does not scale**: `SEARCH_GROUP_LIMIT` is 5, so at most
// ten rows come back. `./label` declared the narrow three-field
// `cardLabelSchema` precisely because *that* array scales with board size. The
// discriminating question is not "is this a lot of fields" but "how many of
// these will one response carry".
// ---------------------------------------------------------------------------

/**
 * One board in the `boards` group — and the board behind every card in the
 * `cards` group.
 *
 * `boardSchema` plus the assembled permalink, on a **copy**.
 *
 * ---------------------------------------------------------------------------
 * ⚠ IT IS FIELD-FOR-FIELD `boardAddressSchema` AND IS DELIBERATELY NOT IT
 * ---------------------------------------------------------------------------
 * `./board` already declares `boardSchema.extend({ path })` under that name.
 * This is not that schema and must not be replaced by it: `boardAddressSchema`
 * is the **response of `GET /boards/by-short-link/:shortLink`**, and reusing it
 * here would mean a field added for the short-link resolver's sake silently
 * changes what search returns, and vice versa. Two routes with the same shape
 * today is not two routes with the same contract. The duplication is visible,
 * cheap and one line; the coupling would be invisible.
 *
 * ⚠ **The same schema is nested inside `searchCardResultSchema.board`**, and
 * that is `cardAddressSchema`'s precedent applied deliberately: *"nesting the
 * view rather than copying two fields out of it also means the board a card
 * resolves through is described by the same schema as a board resolved
 * directly."* A card result's board and a board result are the same object seen
 * from two rows of one dropdown, and a client rendering "in board Marketing"
 * twice must not have to render it two ways.
 *
 * ---------------------------------------------------------------------------
 * ⚠ `visibility` IS ON THE WIRE AND IT IS NOT A LEAK
 * ---------------------------------------------------------------------------
 * It arrives with `boardSchema` and is kept. Every board in this payload is one
 * the caller is a member of, administers the workspace of, or which is
 * `workspace_visible` to them — `isBoardSearchableBy`'s three arms, with the
 * public arm deliberately absent (`FR-SEARCH-018` AC3, `OQ-8`) — and all three
 * populations can already read the value from `GET /boards/:id`. There is no
 * board here whose visibility the caller could not have asked for directly.
 *
 * `closed` likewise arrives and is likewise kept, and today it is **always
 * `false`**: closed boards are excluded by default (`FR-SEARCH-028` AC4's
 * "exclude closed boards" toggle is V1, so the default is on). ⚠ That does not
 * make the field dead — AC4's toggle is exactly what makes it vary, and the
 * block that ships the toggle changes a `WHERE` rather than this shape. **Do
 * not "tidy" a structurally-constant field away**; the same reasoning applies
 * to `archived` below.
 */
export const searchBoardResultSchema = boardSchema.extend({
  /**
   * `/b/<shortLink>/<slug>` — built by `boardPath()` in `./address` and never
   * assembled by hand. That module's rule is that nobody parses a permalink
   * apart; the corollary is that nobody *builds* one apart either, or the URL
   * grammar acquires a second producer that drifts silently, since the slug is
   * ignored on lookup and nothing ever fails.
   */
  path: z.string().min(1),
});

export type SearchBoardResult = z.infer<typeof searchBoardResultSchema>;

/**
 * One card in the `cards` group.
 *
 * `cardSchema` plus the two containers a result row needs to be legible and
 * navigable, on a **copy**.
 *
 * ---------------------------------------------------------------------------
 * WHY BOTH CONTAINERS, AND WHICH ACCEPTANCE CRITERION ASKS FOR EACH
 * ---------------------------------------------------------------------------
 *   - `board` — `FR-SEARCH-015` AC2, verbatim: *"Given a card result is shown,
 *     when the user reads it, then **the owning board name is visible on the
 *     result row**."* A result set spans boards by definition
 *     (`FR-SEARCH-014`: "the input is never scoped to the current board"), so
 *     two cards called "Renew the licence" are indistinguishable without it.
 *     `cardSchema.boardId` cannot render a name.
 *   - `list` — `FR-SEARCH-015`'s behaviour sentence: *"each with enough context
 *     to disambiguate (**a card shows its board and list**)"*. Two cards with
 *     the same title on the *same* board are the case the board name does not
 *     separate.
 *
 * Both are nested objects rather than a second request, because the alternative
 * is N+1 fetches on the keystroke path of a typeahead — the same argument
 * `cardAddressSchema` makes for nesting its board, one surface along.
 *
 * ⚠ `boardId` and `board.id` are both present, as are `listId` and `list.id`,
 * and they always agree — the route reads them from one row. That is
 * `cardDetailSchema`'s established shape (`listId` beside `list`) and is not
 * redundancy to be tidied: the scalar is the key a client compares and caches
 * on, the object is what it renders.
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ TWO INHERITED FIELDS A CLIENT MUST NOT MISREAD
 * ---------------------------------------------------------------------------
 * **`position`** comes along from `cardSchema` and is a lexorank that is only
 * meaningful *within its own list*. Sorting this array by it is meaningless —
 * ranks from different lists on different boards are not comparable, and
 * `compareRanks` will happily return an answer. **The response array is already
 * in the intended order** — `created_at DESC, id DESC` (`FR-SEARCH-019` AC1:
 * *"given two equally matching cards … the more recently created card is listed
 * first"*), which is why `createdAt` is on the wire and is the only field this
 * array may be re-sorted by. A client that re-sorts by anything else breaks
 * `FR-SEARCH-017`'s edge case, which requires the dropdown and the future
 * results page to agree on ranking.
 *
 * **`archived`** is inherited and is today **always `false`**: `FR-SEARCH-028`
 * AC1 excludes archived cards by default, and the route excludes them by *two*
 * conjuncts rather than one — the card's own `archived_at` **and its parent
 * list's**, because B11's standing invariant is that archiving a list writes
 * nothing to `cards.archived_at` (`FR-SEARCH-028`'s edge case: *"Cards archived
 * indirectly by archiving their list must be findable the same way"*). ⚠ The
 * field is kept for the reason `searchBoardResultSchema.closed` is: AC2's
 * "include archived" toggle is what makes it vary, and the shape should not
 * have to change when that block ships.
 */
export const searchCardResultSchema = cardSchema.extend({
  /**
   * The board this card is on — the same shape a board *result* is, so one
   * renderer serves both rows of the dropdown.
   */
  board: searchBoardResultSchema,
  /**
   * The list this card is in — `cardListRefSchema` from `./card-detail`,
   * reused rather than re-declared as `{ id, title }`.
   *
   * ⚠ **Its `archived` is `false` on every row this route returns today**, for
   * the reason given on `searchCardResultSchema.archived`: a card whose list is
   * archived is excluded by the list-side conjunct. Reusing the three-field
   * shape anyway is deliberate — `FR-SEARCH-028` AC2's include-archived toggle
   * makes it vary, and *at that moment the field is the difference between a
   * result the user can act on and one they cannot*. Declaring a two-field
   * shape here would mean the block that ships the toggle has to widen a view
   * as well as loosen a `WHERE`.
   *
   * ⚠ This is `./card-detail`'s shape and **not** its *reasoning*. B14
   * deliberately omits the parent list's `archived_at` conjunct so that a
   * permalink to a card in an archived list still resolves; B20 carries it, on
   * B13's board-face precedent, because a search answers "what is findable"
   * rather than "what is this card". Same field, opposite decision about the
   * `WHERE`, and `apps/server/src/routes/search.ts` names which precedent it
   * followed.
   */
  list: cardListRefSchema,
});

export type SearchCardResult = z.infer<typeof searchCardResultSchema>;

/**
 * What `GET /search` answers with — `FR-SEARCH-015`'s grouped dropdown.
 *
 * ⚠ **Both keys are always present and an empty array is the ordinary answer**,
 * never an omitted key. A client must not read absence and emptiness as the
 * same thing, because only one of them can happen: a query matching no board
 * returns `boards: []`, and `FR-SEARCH-015` AC4's explicit empty state is the
 * rendering of *both* arrays being empty. This is `cardDetailSchema.members`'
 * rule applied to a response with two arrays instead of one.
 *
 * ⚠ **There is no third key of any kind** — no `total`, no `hasMore`, no
 * `query` echoed back, no `truncated` flag. The first two are
 * `FR-SEARCH-018`'s edge case (see the header); the echoed query would invite a
 * client to render the server's copy instead of the user's own input, which
 * lags by one round trip on every keystroke.
 *
 * ⚠ **The two arrays are independently capped at `SEARCH_GROUP_LIMIT` and are
 * independently ordered.** Neither is a subset of the other's ranking, and
 * `cards[0]` is not "the best result" — it is the best *card*. There is no
 * cross-group ranking in this block and `FR-SEARCH-015` asks for none; a
 * client's keyboard navigation (`FE-20.2`) walks a concatenation it composes
 * itself.
 */
export const searchResultsSchema = z.object({
  /**
   * Matching cards, newest first, at most `SEARCH_GROUP_LIMIT`.
   *
   * `FR-SEARCH-019` AC1's recency default, spelled `created_at DESC, id DESC`.
   * ⚠ The `id` tie-break is not decoration: `cards.created_at` defaults to
   * `now()`, which inside a transaction is the *transaction* timestamp, so
   * same-millisecond ties are genuine — the trap `./comment`'s cursor
   * documents, reached here through a different door. Without it the order of
   * tied rows is whatever the planner returns, and a dropdown that reshuffles
   * between two identical searches is a bug with no error attached.
   *
   * ⚠ **No similarity term in the ordering, deliberately.** `pg_trgm`'s
   * `similarity()` is available and is not used: `FR-SEARCH-019` names recency
   * as the default, and `FR-SEARCH-017`'s edge case requires the dropdown and
   * the results page to agree on ranking — so a similarity term added here
   * would have to be inherited or contradicted by B33, which owns that page and
   * has not decided. Measured, for the record: threshold 0.3, and
   * `similarity('Refactor the abc parser for the new pipeline', 'abc')` is
   * 0.102564104, so similarity ranking would also have to be decoupled from the
   * `%` operator's cutoff before it meant anything here.
   */
  cards: z.array(searchCardResultSchema),
  /**
   * Matching boards, newest first, at most `SEARCH_GROUP_LIMIT`. Same ordering
   * and the same tie-break as `cards`, from `boards.created_at`.
   */
  boards: z.array(searchBoardResultSchema),
});

export type SearchResults = z.infer<typeof searchResultsSchema>;

// ---------------------------------------------------------------------------
// GET /search/results — block B33, the full-text results page.
//
// Everything above this line is B20's and is untouched. This section adds a
// second route rather than widening `GET /search`: the dropdown is a fixed,
// five-per-group jump-to (`FR-SEARCH-015`) and the results page is
// `FR-SEARCH-017`'s "see everything" surface over `FR-SEARCH-021`'s four text
// sources (title, description, comment bodies, checklist-item text) — two
// different questions with two different backing indexes
// (`0019`'s trigram GIN on titles for the dropdown, `0025`'s tsvector GIN on
// `card_search` for this one), so they get two schemas and two routes.
// ---------------------------------------------------------------------------

/**
 * How many rows `GET /search/results` returns, full stop.
 *
 * ---------------------------------------------------------------------------
 * ⚠ A FIXED CAP, NOT A PAGE SIZE — AND THE ABSENT `total` IS A SCOPE DECISION,
 * NOT AN OVERSIGHT
 * ---------------------------------------------------------------------------
 * `searchFullResultsSchema` carries no `total`, no `hasMore` and no cursor, for
 * the identical reason `searchResultsSchema` above carries none:
 * `FR-SEARCH-018`'s edge case — *"result counts must not reveal the existence
 * of hidden matches"* — applies to this route exactly as it applies to the
 * dropdown, and a total honest enough to be useful is computed before the
 * `LIMIT`, over rows the caller cannot see.
 *
 * No shipped acceptance criterion for `FR-SEARCH-017` asks for real pagination
 * (a cursor, a page token, a "load more"); it asks for a bigger single page
 * than the dropdown's five. A hundred is generous for that — the dropdown's
 * `SEARCH_GROUP_LIMIT` (5) is a *jump-to* budget, this is a *browse* budget —
 * and a hundred rows is not a number anyone has measured against the planner
 * the way `SEARCH_QUERY_MIN_LENGTH` was; it is a round, generous ceiling
 * chosen because nothing in the corpus asks for more. If a later block needs
 * true paging it needs its own cursor and its own review of what a page
 * boundary leaks, on `./comment`'s precedent, not a `?limit=` bolted onto this
 * one.
 */
export const SEARCH_RESULTS_LIMIT = 100;

/**
 * The querystring of `GET /search/results` — `BE-33.2` and `BE-33.3`.
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ `q` HAS A MINIMUM OF **1**, NOT `SEARCH_QUERY_MIN_LENGTH` (3) — THIS IS A
 * DIFFERENT INDEX WITH A DIFFERENT FLOOR, NOT A LOOSER RULE FOR THE SAME ONE
 * ---------------------------------------------------------------------------
 * `SEARCH_QUERY_MIN_LENGTH` is measured against `0019`'s `gin_trgm_ops` index:
 * `pg_trgm` extracts whole trigrams from the `LIKE` *pattern*, and `%ab%`
 * contains none, so a two-character query degenerates to a sequential scan.
 * That is a structural property of trigram extraction over a pattern string.
 * It has no analogue here: `0025`'s index is a GIN over a `tsvector`, matched
 * with `websearch_to_tsquery`, which tokenises on *words* rather than
 * trigrams — there is no "pattern too short to contain a complete token" case
 * the way there is no complete trigram in a two-character pattern. Importing
 * `SEARCH_QUERY_MIN_LENGTH` here would be borrowing a number whose derivation
 * does not apply, the exact mistake `./search`'s own header opens by refuting
 * a false claim about itself — restated here so this file does not repeat it
 * a different way.
 *
 * So the floor is `1`, the ordinary "must not be empty" minimum every other
 * required string field in this package carries. Whether that is *safe* —
 * whether a one-character or degenerate query could somehow widen the result
 * set past what the caller may see — was checked empirically before this
 * route was written, against `molt_v1_probe`:
 *
 *     to_tsvector('simple','anything at all') @@ websearch_to_tsquery('simple', '')    → false
 *     to_tsvector('simple','anything at all') @@ websearch_to_tsquery('simple', '???') → false
 *
 * An empty or punctuation-only query compiles to a `tsquery` that matches
 * **zero** rows, never every row — the opposite failure shape from the `LIKE`
 * bug `escapeLikePattern` exists to prevent, where an unescaped `%` matches
 * almost everything. So there is no permission-scoped leak at length 1 and
 * this route needs no extra guard beyond the ordinary scope predicate: a
 * `?q=?` or `?q=a` is a legitimate 200 with `cards: []`, not a 422 and not a
 * security hole. The only consequence is product-shaped, not safety-shaped —
 * a one-character search reads as "no results" rather than "keep typing" —
 * and that is a UX call for whichever block builds the input
 * (`FE-33.1`/`FE-33.2`, PENDING), not a reason to raise this floor.
 *
 * ---------------------------------------------------------------------------
 * ⚠ `q` REUSES `SEARCH_QUERY_MAX_LENGTH` (512) TOO, AND ITS DERIVATION DOES
 * **NOT** CARRY OVER THE WAY THE MIN-LENGTH REFUSAL ABOVE MIGHT SUGGEST
 * ---------------------------------------------------------------------------
 * Be precise about what is being reused and what is not: the argument above
 * refuses `SEARCH_QUERY_MIN_LENGTH` because its derivation (a two-character
 * `LIKE` pattern contains no complete trigram) is specific to `0019`'s index
 * and does not describe `0025`'s. `SEARCH_QUERY_MAX_LENGTH`'s own derivation
 * has exactly the same problem here and this schema imports it anyway — so
 * this note exists to say plainly that the reuse is **not** load-bearing the
 * way `./search`'s is.
 *
 * `SEARCH_QUERY_MAX_LENGTH`'s header derives 512 as `max(CARD_TITLE_MAX_LENGTH,
 * BOARD_TITLE_MAX_LENGTH)` — "the bound past which the answer is *provably*
 * empty", because matching is containment against a **bounded `varchar`
 * title**. That property does not hold here: two of this route's four sources,
 * `cards.description` and `comments.body`, are unbounded Postgres `text`
 * columns (`apps/server/src/db/schema.ts`), and `websearch_to_tsquery` is a
 * token match rather than a containment test in the first place — a query
 * longer than 512 characters is not provably unable to match a long
 * description or comment the way it is provably unable to match a 512-`varchar`
 * title. So unlike on `./search`, this cap is **not proven safe against losing
 * a result** — a caller who pastes a genuinely longer passage they remember
 * from a card's description would get a 422 for a query that could have
 * matched.
 *
 * It is kept anyway, as a **pragmatic, not a provable**, ceiling: no shipped
 * `FR-SEARCH-*` acceptance criterion asks this route to accept an arbitrarily
 * long query, reusing the constant keeps the two search routes' upper input
 * bound visibly consistent rather than introducing a second unexplained
 * number, and a search box pasted with more than 512 characters of free text
 * is a vanishingly rare and recoverable case (the caller can search a shorter
 * fragment of the same passage) rather than a correctness bug. If a future
 * block needs this route to accept longer input, that is a deliberate decision
 * to raise this bound with its own justification — not evidence that reusing
 * it here today was an oversight.
 */
export const searchFullResultsQuerySchema = z.strictObject({
  q: z
    .string()
    .trim()
    .min(1)
    .max(SEARCH_QUERY_MAX_LENGTH)
    .describe(
      'The text to search for, matched against a card’s title, ' +
        'description, comment bodies and checklist-item text (FR-SEARCH-021). ' +
        'Tokenised and matched as whole words via websearch_to_tsquery, not a ' +
        'substring match like GET /search’s dropdown — quoted phrases and ' +
        '-exclusions in the text follow that function’s own syntax. ' +
        'Minimum 1 character after trimming: unlike the dropdown, there is no ' +
        'trigram floor here, and an empty or punctuation-only query matches ' +
        'nothing rather than everything. Maximum 512, reused from the ' +
        'dropdown’s bound for consistency — unlike there, this is a ' +
        'pragmatic ceiling rather than a provably-safe one, since ' +
        'descriptions and comments are unbounded text rather than a ' +
        'bounded title. Also accepts the FR-SEARCH-020–FR-SEARCH-027 ' +
        'operator grammar (board:, list:, member:/@handle, is:, has:, ' +
        'due:, created:, edited:, sort:, label:/#, and a leading - to ' +
        'negate any of them) mixed in with the free text — parsed by ' +
        'parseSearchQuery below, whose recognised-token portion is removed ' +
        'before what remains reaches websearch_to_tsquery.',
    ),
  /**
   * `FR-SEARCH-028` AC1's default, made a caller-visible toggle for the first
   * time — `BE-33.3`. `z.stringbool` with the sets **pinned**, `./notification`'s
   * exact reasoning restated: `z.coerce.boolean()` would make `?includeArchived
   * =false` truthy (`Boolean('false') === true`), a filter silently doing the
   * opposite of what was asked, and an unpinned `z.stringbool()` accepts
   * `1`/`yes`/`on`/`y`/`enabled` as spellings of true that no client here emits.
   */
  includeArchived: z
    .stringbool({ truthy: ['true'], falsy: ['false'] })
    .default(false)
    .describe(
      'true to include archived cards and cards whose list is archived, ' +
        'false (the default) to exclude both — FR-SEARCH-028 AC1/AC2.',
    ),
  /**
   * `FR-SEARCH-028` AC4's default, the same toggle treatment as
   * `includeArchived` immediately above and for the identical reason.
   */
  includeClosedBoards: z
    .stringbool({ truthy: ['true'], falsy: ['false'] })
    .default(false)
    .describe(
      'true to include cards on closed boards, false (the default) to ' +
        'exclude them — FR-SEARCH-028 AC4.',
    ),
});

export type SearchFullResultsQuery = z.infer<typeof searchFullResultsQuerySchema>;

/**
 * What `GET /search/results` answers with.
 *
 * ---------------------------------------------------------------------------
 * ⚠ NO `boards` GROUP — DELIBERATELY, AND NOT MERELY "NOT YET"
 * ---------------------------------------------------------------------------
 * `searchResultsSchema` above has two groups because `FR-SEARCH-015` searches
 * two nouns' titles. `FR-SEARCH-021` — the requirement this schema answers —
 * is entirely about **card** content: title, description, comment bodies and
 * checklist-item text. A board has none of the last two, and its title is
 * already served by the existing title-only dropdown. There is no acceptance
 * criterion anywhere in `FR-SEARCH-021` asking for a board's *description* or
 * any other board field to be full-text searched, so there is nothing here
 * for a `boards` group to hold that `0019`'s index does not already cover.
 *
 * If a future block wants full-text matching over board content, that is a
 * new index over new source columns and a new decision about what "matches a
 * board" means for that block to make — not a silent widening of `card_search`
 * (which is keyed on `card_id` and could not hold a board row at all) and not
 * an empty `boards: []` added here on spec.
 */
export const searchFullResultsSchema = z.object({
  /**
   * Matching cards, ordered exactly as `searchResultsSchema.cards` is —
   * `created_at DESC, id DESC` — and capped at `SEARCH_RESULTS_LIMIT` rather
   * than `SEARCH_GROUP_LIMIT`. See `apps/server/src/routes/search-results.ts`
   * for why relevance is not the ordering despite the stored tsvector
   * carrying `setweight` ranks that would make it possible.
   */
  cards: z.array(searchCardResultSchema),
});

export type SearchFullResults = z.infer<typeof searchFullResultsSchema>;

// ---------------------------------------------------------------------------
// FE-33.1's contract residual — the description-source toggle and the two date
// fields the results page has been blocked on. `FR-SEARCH-017`.
//
// Everything above this line is B20's, B33's and B78's and is BYTE-IDENTICAL.
// This section adds four exports and widens nothing:
//
//   `searchFullCardResultSchema`      — `searchCardResultSchema` + dueAt/completed
//   `searchFullResultsPageSchema`     — `{ cards }` over the wider row
//   `searchFullResultsPageQuerySchema`— the shipped querystring + matchDescriptions
//
// ⚠⚠ WHY FOUR NEW EXPORTS RATHER THAN THREE FIELDS ADDED IN PLACE — MEASURED,
// NOT ASSUMED
// ---------------------------------------------------------------------------
// `searchCardResultSchema` serialises **three** routes, counted with Python
// over the whole tree rather than with `grep` (which is a ugrep wrapper with
// `-I` on this machine and returns a false clean on a file carrying a NUL
// byte):
//
//   GET /search                    apps/server/src/routes/search.ts
//                                    → `searchResultsSchema.cards`
//   GET /search/results            apps/server/src/routes/search-results.ts
//                                    → `searchFullResultsSchema.cards`
//   GET /saved-searches/:id/results apps/server/src/routes/saved-searches.ts
//                                    → `savedSearchRunSchema.results` →
//                                      `searchFullResultsSchema.cards`
//
// Widening it in place would put `dueAt` and `completed` on the **dropdown**
// (`FR-SEARCH-015`), which asks for neither and whose own header above argues
// at length against carrying a field a result row does not render.
//
// `searchFullResultsQuerySchema` is worse still: `apps/mcp/src/tools/search.ts`
// re-exports it *whole* as `molt_search`'s input schema and its handler
// destructures exactly three keys, so a fourth key added in place would be
// advertised to a model in the tool's JSON Schema and then never forwarded — a
// tool that accepts an instruction and silently drops it. `apps/mcp/` cannot be
// edited from this chunk, so the drop could not be repaired here.
//
// ⚠ AND ONE THING THIS SECTION DELIBERATELY DID **NOT** DO: it does not
// re-point `searchFullResultsSchema.cards` at the wider row. That was the
// original plan and it is not executable — `apps/web/src/entry-surfaces.
// composition.test.tsx` builds a `SearchFullResults` fixture as
// `{ cards: searchResultsFixture().cards }` from the *dropdown's* fixture, so
// two new required fields on that array make a file outside this chunk's lists
// fail `tsc`. `searchFullResultsSchema` therefore stays exactly as B33 shipped
// it and `GET /search/results` moves to `searchFullResultsPageSchema` instead.
// The saved-search run keeps the narrow shape, which is what `FR-SEARCH-029`
// wants anyway (see `searchFullResultsPageQuerySchema.matchDescriptions`).
// ---------------------------------------------------------------------------

/**
 * One row of `GET /search/results` — `searchCardResultSchema` plus the two
 * fields `FR-SEARCH-017`'s edge case needs, *"Cards whose due date is marked
 * complete are visually distinguished in the result list"*.
 *
 * ⚠ **An extension on a copy, never a widening.** See the section header for
 * the three routes `searchCardResultSchema` serialises and why the dropdown
 * must not gain these two fields.
 *
 * ⚠ **`startAt` is NOT here, and its absence is the decision.** The edge case
 * names a *due* date and a completion flag; no requirement on this surface
 * asks for a start date. `cardSchema`'s own module header states this file's
 * rule — a field added "while we are in the file", against no route and no
 * requirement, is how an unreviewable object model happens. Two fields, both
 * cited, and no third.
 *
 * ⚠ **This is a PROJECTION, not new storage.** `cards.due_at timestamptz(3)
 * NULL` and `cards.completed boolean NOT NULL DEFAULT false` shipped with B24
 * and were read out of the live catalogue before this schema was written.
 * There is no migration behind this file and there must not be one.
 */
export const searchFullCardResultSchema = searchCardResultSchema.extend({
  /**
   * `cards.due_at timestamptz(3) NULL` — `FR-CARD-013`. `null` means the card
   * has no due date, which is the ordinary state: most cards carry none.
   *
   * The spelling is copied verbatim from `./board-contents`'s
   * `boardFaceCardSchema.dueAt` and `./card-detail`'s `cardDetailSchema.dueAt`
   * — Postgres' own timestamp rendering read back through `pg`, never
   * `z.iso.datetime()`. Those two files carry the argument in full; it is not
   * re-decided here, and a third spelling of one column would be the defect.
   */
  dueAt: z.string().min(1).nullable(),
  /** `cards.due_all_day` — FR-EXP-029 edge case (BE-44.7). */
  dueAllDay: z.boolean(),
  /**
   * `cards.completed boolean NOT NULL DEFAULT false` — `FR-CARD-004`, and the
   * "marked complete" half of `FR-SEARCH-017`'s edge case.
   *
   * ⚠ **Not nullable, and independent of `dueAt` beside it.** The column is
   * `NOT NULL DEFAULT false`, so every card has an answer and there is no
   * third state. And `FR-CARD-004`'s own sentence — *"Completion is
   * independent of due dates — a card with no due date can be marked
   * complete"* — makes `completed: true` with `dueAt: null` an ordinary
   * combination rather than an inconsistency. Confirmed from the enforcement
   * side too: there is **no CHECK** on `cards` tying the two columns
   * together, which is what makes the pair legal rather than merely
   * undocumented. A renderer must therefore handle all four combinations, and
   * in particular must not read `completed` as "the due date passed".
   */
  completed: z.boolean(),
});

export type SearchFullCardResult = z.infer<typeof searchFullCardResultSchema>;

/**
 * What `GET /search/results` answers with — the same single `cards` key
 * `searchFullResultsSchema` carries, over the wider row.
 *
 * ⚠ **Still no `total`, no `hasMore`, no cursor and no `boards` group.**
 * `FR-SEARCH-018`'s edge case ("result counts must not reveal the existence of
 * hidden matches") and `searchFullResultsSchema`'s own header both apply here
 * unchanged; this schema adds two *card* fields and nothing else at the top
 * level. Pinned by `Object.keys` in `./search.test.ts`, the same way its
 * predecessor is, so an accidental widening fails there rather than only in a
 * route test nobody thinks to update.
 *
 * ⚠ **`z.object`, so it STRIPS an unknown key rather than refusing it** — the
 * inverse of the querystring's `strictObject`. That is what makes this change
 * additive on the wire: a shipped client still parsing the response against
 * `searchFullResultsSchema` keeps working and silently discards the two new
 * fields, rather than failing.
 */
export const searchFullResultsPageSchema = z.object({
  /**
   * Matching cards, ordered and capped exactly as `searchFullResultsSchema.
   * cards` is — `created_at DESC, id DESC`, at most `SEARCH_RESULTS_LIMIT`.
   * The two extra fields change nothing about the ordering, which
   * `FR-SEARCH-019` fixes and `FR-SEARCH-017`'s edge case requires the
   * dropdown to agree with: `dueAt` is **not** a sort key here, and a client
   * that re-sorts by it breaks that agreement. `sort:due` (`FR-SEARCH-025`)
   * is how a caller asks for that ordering, on the server, inside `q`.
   */
  cards: z.array(searchFullCardResultSchema),
});

export type SearchFullResultsPage = z.infer<typeof searchFullResultsPageSchema>;

/**
 * The querystring of `GET /search/results` — `searchFullResultsQuerySchema`
 * plus `FR-SEARCH-017`'s description-source toggle.
 *
 * `FR-SEARCH-017`'s Behaviour, verbatim: *"…and a toggle for whether card
 * descriptions are matched"*. That sentence is the whole source for this
 * parameter; nothing here is reconstructed from a sibling document.
 *
 * ---------------------------------------------------------------------------
 * ⚠ A QUERY PARAMETER, NOT A FILTER FACET, AND NOT A REPEATED PARAM
 * ---------------------------------------------------------------------------
 * Every *facet* on this surface — `board:`, `list:`, `member:`, `label:`,
 * `has:`, `due:`, `created:`, `edited:`, `sort:` — is compiled into the `q`
 * string by `parseSearchQuery` above and travels as an operator token. The
 * repeated-param contract belongs to those array facets. This is a **single
 * boolean about which text source is searched**, which has no operator
 * spelling in `FR-SEARCH-020`–`FR-SEARCH-027`'s grammar (see the B34 section
 * header below on why `description:` is deliberately not a prefix), so it
 * follows `includeArchived` and `includeClosedBoards` — the only two true
 * query parameters this route has ever had — and not the facet machinery.
 *
 * ---------------------------------------------------------------------------
 * ⚠ THE NAME IS `matchDescriptions`, NOT A THIRD `include*`
 * ---------------------------------------------------------------------------
 * `includeArchived` and `includeClosedBoards` both mean *widen the lifecycle
 * scope*: each lifts an exclusion and can only ever add rows. This does the
 * opposite — it **narrows a text source** and can only ever remove rows — so a
 * third `include*` would read as a fourth lifecycle toggle to anyone scanning
 * the querystring. The requirement's own words ("whether card descriptions are
 * matched") give the better name.
 *
 * ---------------------------------------------------------------------------
 * ⚠ DEFAULT `true`, BECAUSE THAT IS WHAT SHIPS TODAY
 * ---------------------------------------------------------------------------
 * `FR-SEARCH-021` matches four sources unconditionally and the description is
 * one of them. An omitted parameter must therefore leave every existing
 * response byte-identical — which is also what keeps `GET /saved-searches/:id/
 * results` unchanged: `saved_searches` has `include_archived` and
 * `include_closed_boards` and **no third boolean column**, persisting this
 * toggle would be DDL, and this chunk writes no migration. So a saved search
 * re-runs with descriptions matched, always, exactly as it does today.
 *
 * ⚠ **`z.stringbool` with the sets PINNED**, `includeArchived`'s exact
 * reasoning one field along rather than a new one: `z.coerce.boolean()` makes
 * `?matchDescriptions=false` **truthy** (`Boolean('false') === true`), a
 * toggle silently doing the opposite of what was asked and nothing red
 * anywhere; and an unpinned `z.stringbool()` accepts `1`/`yes`/`on`/`y`/
 * `enabled`, spellings no client here emits. A spelling this route does not
 * recognise is a 422, never a guess at what the caller meant.
 *
 * ⚠ **`.extend` on a `strictObject` yields a strict object** — so an
 * unrecognised query key is still a 422 here, not a silently ignored one.
 * Asserted in `./search.test.ts` rather than assumed from the Zod version,
 * because it is the property that stops a mistyped `?matchDescription=false`
 * (singular) from reading as "descriptions matched" while the caller believes
 * they turned them off.
 */
export const searchFullResultsPageQuerySchema = searchFullResultsQuerySchema.extend({
  matchDescriptions: z
    .stringbool({ truthy: ['true'], falsy: ['false'] })
    .default(true)
    .describe(
      'true (the default) to match a card’s description along with its ' +
        'title, comment bodies and checklist-item text; false to search ' +
        'every source EXCEPT the description — FR-SEARCH-017’s toggle for ' +
        'whether card descriptions are matched. Narrows the result set, ' +
        'never widens it, and applies only to the free-text portion of q: a ' +
        'query made entirely of operators is matched by its operators ' +
        'either way. Omitting it leaves the response identical to what this ' +
        'route has always returned.',
    ),
});

export type SearchFullResultsPageQuery = z.infer<typeof searchFullResultsPageQuerySchema>;

// ---------------------------------------------------------------------------
// Saved searches and adjacent connected-product search — block B78.
//
// Saved searches persist executable results-page state, not resolved board or
// member ids. Adjacent-product search stays in a sibling response shape rather
// than widening `searchFullResultsSchema`, so existing Molt search callers keep
// the same payload when a member later links an external product.
// ---------------------------------------------------------------------------

export const SAVED_SEARCH_NAME_MAX_LENGTH = 255;

const timestampStringSchema = z.string().min(1).max(64);

export const savedSearchSchema = z.object({
  id: ulidSchema,
  name: z.string().min(1).max(SAVED_SEARCH_NAME_MAX_LENGTH),
  queryText: searchFullResultsQuerySchema.shape.q,
  includeArchived: z.boolean(),
  includeClosedBoards: z.boolean(),
  createdAt: timestampStringSchema,
  updatedAt: timestampStringSchema,
});

export type SavedSearch = z.infer<typeof savedSearchSchema>;

export const savedSearchListSchema = z.strictObject({
  savedSearches: z.array(savedSearchSchema),
});

export type SavedSearchList = z.infer<typeof savedSearchListSchema>;

export const savedSearchCreateBodySchema = z.strictObject({
  name: z.string().trim().min(1).max(SAVED_SEARCH_NAME_MAX_LENGTH),
  queryText: searchFullResultsQuerySchema.shape.q,
  includeArchived: z.boolean().default(false),
  includeClosedBoards: z.boolean().default(false),
});

export type SavedSearchCreateBody = z.infer<typeof savedSearchCreateBodySchema>;

export const savedSearchRenameBodySchema = z.strictObject({
  name: z.string().trim().min(1).max(SAVED_SEARCH_NAME_MAX_LENGTH),
});

export type SavedSearchRenameBody = z.infer<typeof savedSearchRenameBodySchema>;

export const savedSearchRunSchema = z.strictObject({
  savedSearch: savedSearchSchema,
  results: searchFullResultsSchema,
});

export type SavedSearchRun = z.infer<typeof savedSearchRunSchema>;

export const adjacentProductSearchQuerySchema = z.strictObject({
  q: searchFullResultsQuerySchema.shape.q,
  provider: firstPartyIntegrationKeySchema.optional(),
});

export type AdjacentProductSearchQuery = z.infer<typeof adjacentProductSearchQuerySchema>;

export const adjacentProductTabSchema = z.strictObject({
  provider: firstPartyIntegrationKeySchema,
  displayName: z.string().min(1).max(64),
});

export type AdjacentProductTab = z.infer<typeof adjacentProductTabSchema>;

export const adjacentProductSearchResultSchema = z.strictObject({
  id: z.string().min(1).max(255),
  title: z.string().min(1).max(255),
  url: z.url(),
  snippet: z.string().max(512).nullable(),
});

export type AdjacentProductSearchResult = z.infer<
  typeof adjacentProductSearchResultSchema
>;

export const adjacentProductProviderResultsSchema = z.strictObject({
  provider: firstPartyIntegrationKeySchema,
  displayName: z.string().min(1).max(64),
  state: z.enum(['ready', 'not_linked', 'no_access']),
  results: z.array(adjacentProductSearchResultSchema),
});

export type AdjacentProductProviderResults = z.infer<
  typeof adjacentProductProviderResultsSchema
>;

export const adjacentProductSearchResponseSchema = z.strictObject({
  queryText: z.string().max(SEARCH_QUERY_MAX_LENGTH),
  tabs: z.array(adjacentProductTabSchema),
  providers: z.array(adjacentProductProviderResultsSchema),
});

export type AdjacentProductSearchResponse = z.infer<
  typeof adjacentProductSearchResponseSchema
>;

// ---------------------------------------------------------------------------
// GET /search/results' operator grammar — block B34.
//
// Everything above this line is B20's or B33's and is untouched. This section
// is BE-34.1's pure half: a tokeniser and a per-family value check for the
// prefix:value grammar FR-SEARCH-020 through FR-SEARCH-027 describe, with no
// database and no route wired to it yet (that is a later phase's job, over
// GET /search/results — never GET /search, whose own section above stays
// operator-free). It lives here rather than in apps/server for
// `escapeLikePattern`'s reason above it: a query's grammar is a wire format
// the future results-page input and the server must agree on, and this
// package is the one place both can import it from.
//
// ⚠ FR-SEARCH-021's field-scoped operators — name:, description:, comment:,
// checklist: — are deliberately NOT part of this grammar. That requirement's
// four-source match (title, description, comments, checklists) already ships
// unconditionally as free text in B33's GET /search/results; scoping *within*
// that match to one field is a different, narrower behaviour that no BE-34.x
// item names. So `name:foo` is simply not a recognised prefix below — rule 6
// falls it back to literal free text, the same handling FR-SEARCH-020 AC4
// already specifies for a typo'd prefix like `colour:red`, applied here
// rather than special-cased.
// ---------------------------------------------------------------------------

/**
 * A relative-date window shared by `due:`, `created:` and `edited:` — one of
 * the three named units all three accept, or a bare day count (`due:14`).
 * Kept separate from `DueOperatorValue` below because `created:`/`edited:`
 * stop here: neither accepts `overdue`, `complete` or `incomplete`, which
 * describe the due date's own completion toggle and have no analogue for
 * "when a card was made" or "when it was last touched" (FR-SEARCH-024).
 */
export type RelativeDateWindow =
  { unit: 'day' | 'week' | 'month' } | { unit: 'days'; days: number };

/** `due:`'s value — a `RelativeDateWindow`, or one of its three extra forms. */
export type DueOperatorValue =
  RelativeDateWindow | { unit: 'overdue' | 'complete' | 'incomplete' };

/** `is:`'s fixed vocabulary — normalised to lower case at parse time. */
export type IsOperatorValue = 'starred' | 'open' | 'archived';

/**
 * `has:`'s fixed vocabulary. `'attachment'` (singular) is a documented alias
 * for `'attachments'` — FR-SEARCH-023's own edge case — and is folded into it
 * during parsing so nothing downstream has to know the alias exists.
 */
export type HasOperatorValue =
  'attachments' | 'description' | 'cover' | 'members' | 'stickers';

/** `sort:`'s field vocabulary — the value half of a `sort:` token, without its direction. */
export type SortField = 'due' | 'created' | 'edited';

/**
 * `board:<name-or-id>` (FR-SEARCH-022). Whether `value` is a ULID (matched by
 * id) or a name (matched by contains) is a SQL-time question — see
 * `search-results.ts` — this parser only carries the string.
 */
export interface BoardSearchOperator {
  kind: 'board';
  negated: boolean;
  value: string;
}

/**
 * `list:<name>` (FR-SEARCH-022). ⚠ Unlike `board:`'s contains match,
 * FR-SEARCH-022 restricts this to lists "with that name" — an exact,
 * case-insensitive match at SQL time, not a substring one. Stated here so a
 * future reader does not "fix" one operator's matching mode to agree with
 * the other's; they are deliberately different per the requirement's own
 * wording.
 */
export interface ListSearchOperator {
  kind: 'list';
  negated: boolean;
  value: string;
}

/**
 * `member:<value>` and the `@handle`/`@me` shorthand — one shape for both,
 * since it is the SQL layer's `value === 'me'` branch that actually gives
 * `@me` its meaning, and an explicit `member:me` reaches the identical
 * branch by typing the same string directly.
 *
 * ⚠ Only the `@` shorthand is lower-cased by this parser (rule 3 below); an
 * explicit `member:Priya` keeps its typed case — "accepted verbatim" is this
 * file's own spec for board:/list:/member:/label:'s open vocabulary. That
 * asymmetry is real and intentional, not a gap: usernames are compared by
 * folding the *candidate* at SQL time (`mention.ts`'s convention, reused by
 * `search-results.ts`), so the case typed here never changes which account
 * resolves. It only means a literal `member:ME` does not take the
 * `'me'`-is-the-caller shortcut the way `@ME` does — that shortcut is an
 * exact-string check against `'me'`, and this parser does not fold an
 * explicit `member:` value to manufacture that match.
 */
export interface MemberSearchOperator {
  kind: 'member';
  negated: boolean;
  value: string;
}

/** `is:starred` / `is:open` / `is:archived` (FR-SEARCH-022, FR-SEARCH-025). */
export interface IsSearchOperator {
  kind: 'is';
  negated: boolean;
  value: IsOperatorValue;
}

/** The `has:` family (FR-SEARCH-023) — a presence test, independently negatable per token. */
export interface HasSearchOperator {
  kind: 'has';
  negated: boolean;
  value: HasOperatorValue;
}

/** `due:` (FR-SEARCH-024) — the one date operator with the three extra completion-toggle forms. */
export interface DueSearchOperator {
  kind: 'due';
  negated: boolean;
  value: DueOperatorValue;
}

/** `created:` (FR-SEARCH-024) — a backward-looking recency window; no completion-toggle forms. */
export interface CreatedSearchOperator {
  kind: 'created';
  negated: boolean;
  value: RelativeDateWindow;
}

/** `edited:` (FR-SEARCH-024) — same shape as `created:`, a different column downstream. */
export interface EditedSearchOperator {
  kind: 'edited';
  negated: boolean;
  value: RelativeDateWindow;
}

/**
 * `label:<value>` and the `#<value>` shorthand — one shape for both,
 * matching FR-SEARCH-027's own wording ("the `#` shorthand accepts either a
 * colour or a label name"), which draws no distinction between the two
 * spellings beyond how each is typed.
 */
export interface LabelSearchOperator {
  kind: 'label';
  negated: boolean;
  value: string;
}

/**
 * `sort:<value>` (FR-SEARCH-025) — never negatable, hence no `negated` field
 * at all rather than one that would always read `false`. A leading `-`
 * *before* `sort:` itself (`-sort:due`) has no defined meaning — FR-SEARCH-025
 * only reverses direction with the *inner* `-`, immediately after the colon
 * — so that outer form is an invalid token and falls back to free text whole,
 * hyphen included, exactly like any other invalid operator (see
 * `buildPrefixOperator`'s `sort` case).
 *
 * ⚠ `reversed` records a syntactic fact only — "was there a `-` right after
 * the colon" — not a resolved ascending/descending direction. `sort:due`'s
 * bare form means ascending (soonest first) while `sort:created`'s bare form
 * means descending (newest first), so one boolean cannot mean "ascending"
 * uniformly across fields. Mapping `(field, reversed)` to an actual `ORDER
 * BY` direction per field is the SQL-building phase's job, using the
 * per-field defaults FR-SEARCH-025 states — deliberately left undone here so
 * this parser does not have to know what any of its callers will do with the
 * result.
 */
export interface SortSearchOperator {
  kind: 'sort';
  field: SortField;
  reversed: boolean;
}

/**
 * Every recognised operator token, tagged by `kind` — the ten families
 * FR-SEARCH-020 through FR-SEARCH-027 name. `board`/`list`/`member`/`is`/
 * `has`/`due`/`created`/`edited`/`label` all carry `negated` uniformly
 * (FR-SEARCH-026: "negation applies uniformly across the operator
 * families"); `sort` alone does not — see `SortSearchOperator` for why.
 */
export type SearchOperator =
  | BoardSearchOperator
  | ListSearchOperator
  | MemberSearchOperator
  | IsSearchOperator
  | HasSearchOperator
  | DueSearchOperator
  | CreatedSearchOperator
  | EditedSearchOperator
  | LabelSearchOperator
  | SortSearchOperator;

/**
 * `parseSearchQuery`'s return — a raw query already split into its free-text
 * remainder and its recognised operator tokens, in original left-to-right
 * order.
 *
 * ⚠ Declared by hand rather than with `z.infer`, breaking this file's own
 * "declare a schema and derive the type, never both" rule for exactly one
 * reason: there is no schema to infer it from. `parseSearchQuery` is not a
 * wire boundary — nothing ever calls `.parse()` on a raw query string,
 * because a malformed one is defined to fall back to free text rather than
 * fail (rule 6 below) — so a Zod schema here would exist solely to be
 * inferred from, the inverse of every other type in this file and not a
 * reason to add one.
 */
export interface ParsedSearchQuery {
  freeText: string;
  operators: SearchOperator[];
}

/** The ten prefixes rule 5 recognises — anything else is free text (FR-SEARCH-020 AC4). */
const RECOGNISED_OPERATOR_PREFIXES = new Set([
  'board',
  'list',
  'member',
  'is',
  'has',
  'due',
  'created',
  'edited',
  'sort',
  'label',
]);

const RELATIVE_DATE_UNITS = new Set(['day', 'week', 'month']);

/**
 * A bare non-negative integer — `due:14`'s "that many days" form.
 *
 * ⚠ Bounded at 15 digits, not left as an unbounded `\d+`. `q` is up to
 * `SEARCH_QUERY_MAX_LENGTH` (512) characters, so an unbounded pattern would
 * let a value like `due:` followed by hundreds of nines through this check —
 * `Number()` on a digit string past roughly 309 digits returns `Infinity`,
 * silently, with no exception to catch. This file's own header states the
 * point of validating vocabulary here rather than in the SQL-building phase:
 * "the SQL-building phase should be able to trust every operator it receives
 * is already well-formed" — an `Infinity` handed to that phase is not
 * well-formed, and interpolating it into `interval '<N> days'` is exactly
 * the kind of failure a parse-time check is supposed to make impossible
 * rather than merely improbable. Fifteen digits keeps every value inside
 * `Number.MAX_SAFE_INTEGER` (16 digits) with room to spare, while still
 * accepting anything a real caller could mean by "that many days" many
 * times over.
 */
const DAY_COUNT_PATTERN = /^\d{1,15}$/;

function parseRelativeDateWindow(value: string): RelativeDateWindow | null {
  const lower = value.toLowerCase();
  if (RELATIVE_DATE_UNITS.has(lower)) {
    return { unit: lower as 'day' | 'week' | 'month' };
  }
  if (DAY_COUNT_PATTERN.test(value)) {
    return { unit: 'days', days: Number(value) };
  }
  return null;
}

function parseDueValue(value: string): DueOperatorValue | null {
  const lower = value.toLowerCase();
  if (lower === 'overdue' || lower === 'complete' || lower === 'incomplete') {
    return { unit: lower };
  }
  return parseRelativeDateWindow(value);
}

function parseIsValue(value: string): IsOperatorValue | null {
  const lower = value.toLowerCase();
  return lower === 'starred' || lower === 'open' || lower === 'archived' ? lower : null;
}

function parseHasValue(value: string): HasOperatorValue | null {
  const lower = value.toLowerCase();
  if (lower === 'attachment') return 'attachments'; // FR-SEARCH-023's documented singular alias.
  if (
    lower === 'attachments' ||
    lower === 'description' ||
    lower === 'cover' ||
    lower === 'members' ||
    lower === 'stickers'
  ) {
    return lower;
  }
  return null;
}

/** Rule 5's `sort:` value shape — an optional inner `-` immediately after the colon. */
const SORT_VALUE_PATTERN = /^(-)?(due|created|edited)$/i;

function parseSortValue(value: string): { field: SortField; reversed: boolean } | null {
  const match = SORT_VALUE_PATTERN.exec(value);
  if (!match) return null;
  return {
    field: match[2]!.toLowerCase() as SortField,
    reversed: match[1] !== undefined,
  };
}

/** Rule 3's `@handle`/`@me` shape — case-insensitive on the whole token, per FR-SEARCH-020's edge case. */
const HANDLE_SHORTHAND_PATTERN = /^@(me|[a-z0-9._-]+)$/i;

/** Rule 5's `prefix:value` shape, checked before the captured prefix is looked up. */
const PREFIX_VALUE_PATTERN = /^([a-z]+):(.*)$/i;

/**
 * Strip a value's surrounding quotes if it has them, flagging malformed
 * quoting (an opening quote with no matching close within this token) so the
 * caller can fall the whole token back to free text rather than accept a
 * truncated value.
 *
 * ⚠ A quote NOT at position 0 is not "quoted" at all under this check —
 * `board:Mar"keting` is an ordinary, if odd, unquoted value, and this file's
 * open-vocabulary prefixes accept it verbatim rather than reporting it
 * malformed.
 */
function stripValueQuotes(value: string): { value: string; malformed: boolean } {
  if (!value.startsWith('"')) return { value, malformed: false };
  if (value.length >= 2 && value.endsWith('"')) {
    return { value: value.slice(1, -1), malformed: false };
  }
  return { value: '', malformed: true };
}

/**
 * Turn a validated `prefix`/`value`/`negated` triple into the one operator it
 * names, or `null` if `value` fails that prefix's own vocabulary — the caller
 * falls the whole original token back to free text on `null` (rule 6).
 *
 * `sort` alone refuses on `negated` by itself, before even looking at
 * `value`: see `SortSearchOperator`'s own note for why an outer `-` before
 * `sort:` has no defined meaning rather than negating the sort.
 */
function buildPrefixOperator(
  prefix: string,
  value: string,
  negated: boolean,
): SearchOperator | null {
  switch (prefix) {
    case 'board':
      return { kind: 'board', negated, value };
    case 'list':
      return { kind: 'list', negated, value };
    case 'member':
      return { kind: 'member', negated, value };
    case 'label':
      return { kind: 'label', negated, value };
    case 'is': {
      const parsed = parseIsValue(value);
      return parsed === null ? null : { kind: 'is', negated, value: parsed };
    }
    case 'has': {
      const parsed = parseHasValue(value);
      return parsed === null ? null : { kind: 'has', negated, value: parsed };
    }
    case 'due': {
      const parsed = parseDueValue(value);
      return parsed === null ? null : { kind: 'due', negated, value: parsed };
    }
    case 'created': {
      const parsed = parseRelativeDateWindow(value);
      return parsed === null ? null : { kind: 'created', negated, value: parsed };
    }
    case 'edited': {
      const parsed = parseRelativeDateWindow(value);
      return parsed === null ? null : { kind: 'edited', negated, value: parsed };
    }
    case 'sort': {
      if (negated) return null;
      const parsed = parseSortValue(value);
      return parsed === null
        ? null
        : { kind: 'sort', field: parsed.field, reversed: parsed.reversed };
    }
    default:
      // Unreachable: every caller only reaches here for a prefix already
      // confirmed to be in RECOGNISED_OPERATOR_PREFIXES.
      return null;
  }
}

/**
 * Split a raw query on whitespace, except inside a double-quoted run — a
 * quote toggles rather than nests, so this grammar has no notion of escaping
 * one.
 *
 * ⚠ An opened-but-never-closed quote is not an error: once toggled on, every
 * remaining character — including whitespace — is absorbed into the current
 * token, because there is no closing quote left to toggle it back off. That
 * single property of the toggle is what turns "the rest of the string from
 * that quote onward" into one token (rule 1's malformed-quoting case),
 * without a separate branch for it.
 */
function tokenise(raw: string): string[] {
  const tokens: string[] = [];
  let current = '';
  let inQuotes = false;

  for (const character of raw) {
    if (character === '"') {
      inQuotes = !inQuotes;
      current += character;
      continue;
    }
    if (!inQuotes && /\s/.test(character)) {
      if (current.length > 0) {
        tokens.push(current);
        current = '';
      }
      continue;
    }
    current += character;
  }
  if (current.length > 0) tokens.push(current);
  return tokens;
}

/**
 * Parse a raw search query into its recognised operator tokens and its
 * free-text remainder — `BE-34.1`'s pure half.
 *
 * ⚠ Never throws and never produces an error value. Every token that is not
 * one of the ten fixed `prefix:value` families, the `@handle` shorthand or
 * the `#value` shorthand is free text by definition (rule 6) — this is
 * FR-SEARCH-020 AC4's own "unrecognised prefix" handling generalised to
 * cover every way a token can fail to parse: a dangling quote, an empty
 * value, an invalid value for a closed vocabulary and an unknown prefix all
 * fail the identical way, rather than each needing its own error path.
 *
 * No I/O, no clock, no randomness: this function's output for a given input
 * never changes, which is what will let a client preview an operator's
 * effect, or a saved search (`FR-SEARCH-029`, unbuilt) re-parse its stored
 * text, without a round trip to the server.
 */
export function parseSearchQuery(raw: string): ParsedSearchQuery {
  const operators: SearchOperator[] = [];
  const freeTextTokens: string[] = [];

  for (const token of tokenise(raw)) {
    const negated = token.startsWith('-');
    const rest = negated ? token.slice(1) : token;

    let operator: SearchOperator | null = null;

    const handleMatch = HANDLE_SHORTHAND_PATTERN.exec(rest);
    if (handleMatch) {
      operator = { kind: 'member', negated, value: handleMatch[1]!.toLowerCase() };
    } else if (rest.startsWith('#')) {
      const { value, malformed } = stripValueQuotes(rest.slice(1));
      if (!malformed && value.length > 0) {
        operator = { kind: 'label', negated, value };
      }
    } else {
      const prefixMatch = PREFIX_VALUE_PATTERN.exec(rest);
      if (prefixMatch) {
        const prefix = prefixMatch[1]!.toLowerCase();
        if (RECOGNISED_OPERATOR_PREFIXES.has(prefix)) {
          const { value, malformed } = stripValueQuotes(prefixMatch[2]!);
          if (!malformed && value.length > 0) {
            operator = buildPrefixOperator(prefix, value, negated);
          }
        }
      }
    }

    if (operator) {
      operators.push(operator);
    } else {
      // Rule 6: whatever the token was — an ordinary word, an unrecognised
      // prefix, a typo'd value, a dangling quote — it goes back VERBATIM,
      // hyphen included if it had one. This is the one fallback that makes
      // "unparseable" impossible: nothing above ever throws or reports an
      // error, it just fails to become an operator, and a bare `-word` that
      // fails every check above reaches `websearch_to_tsquery` untouched,
      // which is that function's own native exclusion syntax — this parser
      // does not need to implement bare-keyword negation itself.
      freeTextTokens.push(token);
    }
  }

  // `.trim()` guards the aggregate boundary only — an ordinary token is never
  // itself padded with whitespace, but a malformed, unterminated quote at the
  // very start or end of the raw input can absorb trailing whitespace into
  // the one token rule 6 returns verbatim, and that edge should not leak into
  // what reaches `websearch_to_tsquery` as a stray trailing space.
  return { freeText: freeTextTokens.join(' ').trim(), operators };
}
